import { useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { VideoPlayer } from "../components/VideoPlayer";
import { QuizEditor } from "../components/QuizEditor";
import { VideoEditModal } from "../components/VideoEditModal";
import {
  Vault, FilmSlate, BookOpen, UploadSimple, LinkSimple, PlayCircle,
  Trash, Sparkle, PencilSimple, X, ListChecks,
  GoogleLogo, ArrowsClockwise, BellRinging, PlugsConnected,
} from "@phosphor-icons/react";

const ROLE_TAGS = ["all", "server", "bartender", "barista", "line_cook", "housekeeping"];
const CATEGORIES = ["Training", "Kitchen", "Bar", "Service", "Housekeeping", "Safety", "Recipe"];

const EMPTY_INTEG = { google_place_id: "", google_api_key: "", whatsapp_api_key: "", alert_email: "", alert_whatsapp: "", escalation_email_enabled: false, escalation_whatsapp_enabled: false };

function Toggle({ on, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
      <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all ${on ? "left-7" : "left-1"}`} />
    </button>
  );
}

function ConnectorsPanel() {
  const [integ, setInteg] = useState(EMPTY_INTEG);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const loadLog = () => api.get("/notifications").then((r) => setLog(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/org/integrations").then((r) => setInteg({ ...EMPTY_INTEG, ...r.data })).catch(() => {});
    loadLog();
  }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/org/integrations", next || integ);
      setInteg({ ...EMPTY_INTEG, ...data });
      toast.success("Connector & escalation settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const sync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      await api.put("/org/integrations", integ);
      const { data } = await api.post("/reviews/sync-google");
      setSyncResult(data);
      toast.success(`Google sync: ${data.imported} imported, ${data.skipped} skipped`);
    } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
  };

  const inputCls = "w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900";
  return (
    <div className="space-y-6" data-testid="connectors-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5">
        <div>
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><GoogleLogo size={22} weight="duotone" className="text-emerald-600" /> Google Reviews Connector</h2>
          <p className="text-sm text-slate-500 mt-1">Pull live Google reviews — negative feedback becomes briefing action pointers and micro-quizzes automatically.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Google Place ID or Business Profile URL</label>
            <input data-testid="google-place-id-input" value={integ.google_place_id}
              onChange={(e) => setInteg({ ...integ, google_place_id: e.target.value })}
              placeholder="e.g. ChIJN1t_tDeuEmsRUsoyG83frY4" className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Custom Google API key (optional override)</label>
            <input data-testid="google-api-key-input" value={integ.google_api_key}
              onChange={(e) => setInteg({ ...integ, google_api_key: e.target.value })}
              placeholder="Blank = use System Master Key" className={`mt-2 ${inputCls}`} />
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="connector-save-button" onClick={() => save()} disabled={busy}
            className="h-12 px-6 rounded-full border-2 border-slate-200 text-sm font-bold tap-press hover:border-slate-900 disabled:opacity-50">Save settings</button>
          <button data-testid="google-sync-button" onClick={sync} disabled={syncing}
            className="h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-slate-800 disabled:opacity-50">
            <ArrowsClockwise size={18} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync reviews now"}
          </button>
        </div>
        {syncResult && (
          <p data-testid="google-sync-result" className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
            Fetched {syncResult.fetched} · imported {syncResult.imported} · skipped {syncResult.skipped}. {syncResult.note}
          </p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5">
        <div>
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><BellRinging size={22} weight="duotone" className="text-amber-500" /> Escalation Alerts</h2>
          <p className="text-sm text-slate-500 mt-1">Overdue tasks and red-flag handovers trigger email / WhatsApp escalations. Delivery activates once provider keys are configured.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Alert email recipient</label>
            <input data-testid="alert-email-input" type="email" value={integ.alert_email}
              onChange={(e) => setInteg({ ...integ, alert_email: e.target.value })}
              placeholder="ops@yourbrand.com" className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">WhatsApp number (with country code)</label>
            <input data-testid="alert-whatsapp-input" value={integ.alert_whatsapp}
              onChange={(e) => setInteg({ ...integ, alert_whatsapp: e.target.value })}
              placeholder="+91 98765 43210" className={`mt-2 ${inputCls}`} />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Custom WhatsApp API key (optional override)</label>
          <input data-testid="whatsapp-api-key-input" value={integ.whatsapp_api_key}
            onChange={(e) => setInteg({ ...integ, whatsapp_api_key: e.target.value })}
            placeholder="Blank = use platform Twilio keys" className={`mt-2 ${inputCls}`} />
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center justify-between gap-4 flex-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
            <p className="text-sm font-bold">Email escalations</p>
            <Toggle testid="escalation-email-toggle" on={integ.escalation_email_enabled}
              onClick={() => save({ ...integ, escalation_email_enabled: !integ.escalation_email_enabled })} />
          </div>
          <div className="flex items-center justify-between gap-4 flex-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
            <p className="text-sm font-bold">WhatsApp escalations</p>
            <Toggle testid="escalation-whatsapp-toggle" on={integ.escalation_whatsapp_enabled}
              onClick={() => save({ ...integ, escalation_whatsapp_enabled: !integ.escalation_whatsapp_enabled })} />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Delivery log</p>
            <button data-testid="notifications-refresh" onClick={loadLog} className="text-xs font-bold text-slate-500 underline tap-press">Refresh</button>
          </div>
          <div className="space-y-2" data-testid="notifications-log">
            {log.map((n) => (
              <div key={n.id} className="flex items-center gap-3 text-sm bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5">
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-500 shrink-0">{n.channel}</span>
                <p className="flex-1 font-semibold text-slate-700 truncate">{n.subject}</p>
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full shrink-0 ${n.status === "queued" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                  {n.status === "pending_configuration" ? "pending config" : n.status}
                </span>
              </div>
            ))}
            {log.length === 0 && <p className="text-sm text-slate-400">No escalations queued yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BrandStudioPage() {
  const [tab, setTab] = useState("videos");
  const [editVideo, setEditVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [sops, setSops] = useState([]);
  const [mode, setMode] = useState("link");
  const [form, setForm] = useState({ title: "", description: "", role_tag: "all", category: "Training", url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editQuiz, setEditQuiz] = useState([]);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/videos").then((r) => setVideos(r.data.filter((v) => v.scope !== "universal")));
    api.get("/sops").then((r) => setSops(r.data));
  };
  useEffect(() => { load(); }, []);

  const submitVideo = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (mode === "link" && !form.url.trim()) return toast.error("Paste a video link");
    if (mode === "upload" && !file) return toast.error("Choose a video file");
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, mode === "upload" && k === "url" ? "" : v));
      if (mode === "upload" && file) fd.append("file", file);
      await api.post("/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Brand video published — AI quiz generated");
      setForm({ title: "", description: "", role_tag: "all", category: "Training", url: "" });
      setFile(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const removeVideo = async (id) => {
    try { await api.delete(`/videos/${id}`); toast.success("Video removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openQuizEditor = (item, type) => {
    setEditing({ id: item.id, type, title: item.title });
    setEditQuiz(JSON.parse(JSON.stringify(item.quiz || [])));
  };

  const saveQuiz = async () => {
    const clean = editQuiz.filter((q) => q.question.trim());
    try {
      await api.put(editing.type === "video" ? `/videos/${editing.id}/quiz` : `/sops/${editing.id}/quiz`, { quiz: clean });
      toast.success("Quiz updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8 rise-in" data-testid="brand-studio-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Founder Brand Vault</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <Vault size={36} weight="duotone" className="text-emerald-600" /> Brand Studio
        </h1>
        <p className="text-slate-500 mt-3">Your proprietary recipes, SOPs and training videos — with full control over every quiz question.</p>
      </div>

      <div className="flex gap-2">
        <button data-testid="brand-tab-videos" onClick={() => setTab("videos")}
          className={`h-12 px-6 rounded-full text-sm font-bold flex items-center gap-2 tap-press border ${tab === "videos" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
          <FilmSlate size={18} weight="duotone" /> Brand Videos ({videos.length})
        </button>
        <button data-testid="brand-tab-sops" onClick={() => setTab("sops")}
          className={`h-12 px-6 rounded-full text-sm font-bold flex items-center gap-2 tap-press border ${tab === "sops" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
          <BookOpen size={18} weight="duotone" /> SOPs & Recipes ({sops.length})
        </button>
        <button data-testid="brand-tab-connect" onClick={() => setTab("connect")}
          className={`h-12 px-6 rounded-full text-sm font-bold flex items-center gap-2 tap-press border ${tab === "connect" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
          <PlugsConnected size={18} weight="duotone" /> Connectors & Alerts
        </button>
      </div>

      {tab === "connect" && <ConnectorsPanel />}

      {tab === "videos" && (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4">
            <div className="flex gap-2">
              <button data-testid="media-mode-link" onClick={() => setMode("link")}
                className={`h-11 px-5 rounded-full text-sm font-bold flex items-center gap-2 tap-press border ${mode === "link" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>
                <LinkSimple size={18} /> Video link
              </button>
              <button data-testid="media-mode-upload" onClick={() => setMode("upload")}
                className={`h-11 px-5 rounded-full text-sm font-bold flex items-center gap-2 tap-press border ${mode === "upload" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>
                <UploadSimple size={18} /> Upload .mp4
              </button>
            </div>
            <p className="text-xs font-semibold text-slate-400">Tip: external video links are the most reliable source — large uploads can fail on slow networks.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="media-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Video title" className="h-12 px-4 rounded-xl border border-slate-200" />
              {mode === "link" ? (
                <input data-testid="media-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="YouTube / Vimeo / direct .mp4 link" className="h-12 px-4 rounded-xl border border-slate-200" />
              ) : (
                <button data-testid="media-file-button" onClick={() => fileRef.current?.click()}
                  className="h-12 px-4 rounded-xl border-2 border-dashed border-slate-300 text-sm font-bold text-slate-500 tap-press hover:border-slate-900 truncate">
                  {file ? file.name : "Choose .mp4 / .webm / .mov"}
                </button>
              )}
              <input ref={fileRef} type="file" accept=".mp4,.webm,.mov" className="hidden" data-testid="media-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <select data-testid="media-role-select" value={form.role_tag} onChange={(e) => setForm({ ...form, role_tag: e.target.value })}
                className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold capitalize">
                {ROLE_TAGS.map((r) => <option key={r} value={r}>{r === "all" ? "All roles" : r.replace("_", " ")}</option>)}
              </select>
              <select data-testid="media-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <textarea data-testid="media-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Short description — the AI uses this to build the quiz" rows={2}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 resize-none" />
            <button data-testid="media-submit-button" onClick={submitVideo} disabled={busy}
              className="h-14 px-8 rounded-full bg-slate-900 text-white font-heading font-bold flex items-center gap-2 tap-press hover:bg-slate-800 disabled:opacity-50">
              {busy ? <><Sparkle size={20} className="pulse-dot" /> Publishing & generating quiz…</> : <><FilmSlate size={20} weight="duotone" /> Publish brand video</>}
            </button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="brand-video-grid">
            {videos.map((v) => (
              <div key={v.id} data-testid={`brand-video-card-${v.id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-lift">
                <button onClick={() => setPlaying(v)} data-testid={`brand-play-${v.id}`}
                  className="relative w-full h-40 bg-slate-900 flex items-center justify-center tap-press group">
                  <PlayCircle size={56} weight="duotone" className="text-white/90 group-hover:scale-110 transition-transform" />
                </button>
                <div className="p-5">
                  <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-600">{v.category} · {v.role_tag === "all" ? "All roles" : v.role_tag.replace("_", " ")}</span>
                  <h3 className="font-heading font-bold text-lg mt-1 leading-snug">{v.title}</h3>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-xs text-slate-400 font-semibold">{v.quiz?.length || 0} quiz Qs</span>
                    <div className="flex gap-1">
                      <button data-testid={`brand-quiz-edit-${v.id}`} onClick={() => openQuizEditor(v, "video")}
                        className="p-2 text-slate-400 hover:text-slate-900 tap-press" title="Edit quiz"><PencilSimple size={18} /></button>
                      <button data-testid={`brand-edit-video-${v.id}`} onClick={() => setEditVideo(v)}
                        className="p-2 text-slate-300 hover:text-slate-900 tap-press"><PencilSimple size={18} /></button>
                      <button data-testid={`brand-delete-${v.id}`} onClick={() => removeVideo(v.id)}
                        className="p-2 text-slate-300 hover:text-red-500 tap-press"><Trash size={18} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {videos.length === 0 && <p className="text-slate-400 col-span-full">No brand videos yet — publish your first one above.</p>}
          </div>
        </>
      )}

      {tab === "sops" && (
        <div className="space-y-4" data-testid="brand-sop-list">
          <Link to="/sop-studio" data-testid="brand-create-sop-link"
            className="inline-flex h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold items-center gap-2 tap-press hover:bg-slate-800">
            <Sparkle size={18} weight="duotone" /> Create new SOP / recipe in SOP Studio
          </Link>
          <div className="grid sm:grid-cols-2 gap-4">
            {sops.map((s) => (
              <div key={s.id} data-testid={`brand-sop-card-${s.id}`}
                className="bg-white border border-slate-200 rounded-2xl p-6 border-l-4 border-l-emerald-500">
                <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-600">{s.category}</span>
                <h3 className="font-heading font-bold text-lg mt-1 leading-snug">{s.title}</h3>
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-400 font-semibold">
                  <ListChecks size={16} /> {s.steps.length} steps · {s.quiz?.length || 0} quiz Qs
                </div>
                <button data-testid={`brand-sop-quiz-edit-${s.id}`} onClick={() => openQuizEditor(s, "sop")}
                  className="mt-4 h-11 px-5 rounded-full border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-2 tap-press hover:border-slate-900">
                  <PencilSimple size={16} /> Edit quiz
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {playing && <VideoPlayer video={playing} onClose={() => setPlaying(null)} />}

      {editing && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setEditing(null)}>
          <div data-testid="quiz-editor-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-2xl font-bold">Quiz Editor</h2>
              <button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-6">{editing.title} — override or extend the AI-generated questions.</p>
            <QuizEditor quiz={editQuiz} onChange={setEditQuiz} />
            <button data-testid="quiz-editor-save" onClick={saveQuiz}
              className="mt-6 w-full h-14 rounded-full bg-emerald-600 text-white font-heading font-bold tap-press hover:bg-emerald-700">
              Save quiz
            </button>
          </div>
        </div>
      )}
      {editVideo && <VideoEditModal video={editVideo} onClose={() => setEditVideo(null)} onSaved={() => load()} />}
    </div>
  );
}
